/**
 * Renders GFM markdown for comment card bodies (user + AI) in the document comment sidebar.
 */
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components/macro";
import { themeVariables } from "../../themeVariables";

const MarkdownRoot = styled.div`
  min-width: 0;

  & p {
    margin: 0 0 0.5rem 0;
    color: inherit;
  }
  & p:last-child {
    margin-bottom: 0;
  }

  & h1,
  & h2,
  & h3 {
    margin: 0.75rem 0 0.5rem 0;
    color: inherit;
    font-weight: 600;
    line-height: 1.25;
  }
  & h1 {
    font-size: 1.25rem;
  }
  & h2 {
    font-size: 1.125rem;
  }
  & h3 {
    font-size: 1rem;
  }
  & h1:first-child,
  & h2:first-child,
  & h3:first-child {
    margin-top: 0;
  }

  & ul,
  & ol {
    margin: 0 0 0.5rem 0;
    padding-left: 1.25rem;
    color: inherit;
  }
  /* App.scss globally clears list item markers; restore them for GFM lists */
  & li {
    display: list-item;
    list-style: inherit;
    margin: 0.25rem 0;
  }
  & ul {
    list-style: disc;
  }
  & ol {
    list-style: decimal;
  }
  & ul ul {
    list-style: circle;
  }

  & code {
    font-family: ui-monospace, Menlo, Monaco, "Consolas", monospace;
    font-size: 0.875em;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    background-color: ${themeVariables.grey4};
  }

  & pre {
    margin: 0 0 0.5rem 0;
    padding: 0.75rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    background-color: ${themeVariables.grey4};
  }
  & pre:last-child {
    margin-bottom: 0;
  }
  & pre code {
    padding: 0;
    background: none;
  }

  & blockquote {
    margin: 0 0 0.5rem 0;
    padding-left: 0.75rem;
    border-left: 2px solid ${themeVariables.grey3};
    color: ${themeVariables.grey1};
  }

  & a {
    color: ${themeVariables.primaryCTAHighContrast};
    text-decoration: underline;
  }

  & hr {
    border: 0;
    border-top: 1px solid ${themeVariables.grey3};
    margin: 0.75rem 0;
  }

  & table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  & th,
  & td {
    border: 1px solid ${themeVariables.grey3};
    padding: 0.25rem 0.5rem;
  }
  & th {
    text-align: left;
    background-color: ${themeVariables.grey4};
  }
`;

const TableScroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
  margin: 0 0 0.5rem 0;
`;

const PlainLinkText = styled.span`
  color: inherit;
  text-decoration: none;
`;

const SudowriteMentionText = styled.span`
  color: ${themeVariables.primaryCTAHighContrast};
  font-weight: 400;
`;

function safeHref(href: string | undefined): string | undefined {
  if (typeof href !== "string") return undefined;
  const t = href.trim();
  if (/^https?:\/\//i.test(t) || /^mailto:/i.test(t)) return t;
  return undefined;
}

/**
 * Wraps a well-formed `@sudowrite` tag in markdown link markup so it can be styled
 * as a high-contrast CTA mention in the sidebar.
 * Requires start / whitespace / common punctuation before `@` so `user@sudowrite.com` is not matched.
 * Skips occurrences already wrapped as markdown links.
 */
export function boldSudowriteMentionsForCommentMarkdown(source: string): string {
  if (!source || typeof source !== "string") return source;
  return source.replace(/(^|[\s,;(>"'<])(?!\]\()@sudowrite\b/gi, (_, pre: string) => `${pre}[@sudowrite](sudowrite-mention://local)`);
}

export const CommentCardMarkdown: React.FC<{
  /** Raw markdown (also used when the user has not used markdown). */
  source: string;
}> = ({ source }) => {
  const markdownSource = useMemo(() => boldSudowriteMentionsForCommentMarkdown(source), [source]);

  const components = useMemo(
    () => ({
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
        const childText =
          typeof children === "string"
            ? children
            : Array.isArray(children) && children.length === 1 && typeof children[0] === "string"
              ? children[0]
              : null;
        const isSudowriteMentionText =
          typeof childText === "string" && /^@sudowrite$/i.test(childText.trim());

        if ((typeof href === "string" && href.startsWith("sudowrite-mention://")) || isSudowriteMentionText) {
          return <SudowriteMentionText>{children}</SudowriteMentionText>;
        }
        const to = safeHref(href);
        if (!to) {
          return <PlainLinkText>{children}</PlainLinkText>;
        }
        return (
          <a href={to} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
      table: ({ children }: { children?: React.ReactNode }) => (
        <TableScroll>
          <table>{children}</table>
        </TableScroll>
      ),
    }),
    [],
  );

  return (
    <MarkdownRoot data-testid="comment-card-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdownSource}
      </ReactMarkdown>
    </MarkdownRoot>
  );
};

export default CommentCardMarkdown;
